import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      if (res.status === 429) {
        setError('Too many failed login attempts. Please try again later.');
      } else if (res.status === 401) {
        setError('Invalid username or password.');
      } else if (res.ok) {
        const redirect = searchParams.get('redirect');
        if (redirect && redirect.startsWith('/')) {
          navigate(redirect, { replace: true });
        } else {
          navigate('/admin/review-requests');
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/tapelligence_logo_transparent.png" alt="Tapelligence" className="h-[37px] mx-auto mb-6" loading="eager" />
          <h1 className="text-xl font-bold text-slate-900" data-testid="login-title">Admin Login</h1>
          <p className="text-sm text-slate-500 mt-1">Internal access only</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
          data-testid="login-form"
        >
          {error && (
            <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg" data-testid="login-error">
              <p className="text-xs text-red-600 font-medium">{error}</p>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Username</label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
              autoComplete="username"
              required
              data-testid="login-username"
            />
          </div>

          <div className="mb-6">
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
              autoComplete="current-password"
              required
              data-testid="login-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading || !username || !password}
            className="w-full px-4 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
            data-testid="login-submit-btn"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Logging in...</> : 'Login'}
          </button>
        </form>

        <p className="text-center text-[10px] text-slate-400 mt-6">Tapelligence Internal Systems</p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
